"use client";

import * as React from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { toast } from "sonner";
import { useSiteAuthStore } from "@gerama/stores";
import { isApiError } from "@gerama/types";
import { browserCoreApi } from "@/lib/api";
import { AuthMobileForm } from "./auth-mobile-form";
import { AuthNewPasswordForm, AuthRegisterForm, type RegisterValues } from "./auth-register-form";
import { AuthOtpView } from "./auth-otp-view";
import { AuthPasswordForm } from "./auth-password-form";

type Step =
  | "mobile"
  | "password"
  | "otp-login"
  | "otp-register"
  | "otp-forget"
  | "register"
  | "new-password";

const SESSION = { platform: "web", deviceName: "Web Browser" } as const;
const HISTORY_KEY = "geramaAuthStep";

function previousStep(step: Step): Step {
  switch (step) {
    case "password":
    case "otp-register":
      return "mobile";
    case "otp-login":
    case "otp-forget":
      return "password";
    case "new-password":
      return "otp-forget";
    case "register":
      return "otp-register";
    default:
      return "mobile";
  }
}

function errorText(error: unknown, fallback: string): string {
  return isApiError(error) ? error.message : fallback;
}

/**
 * The same stages on the login page and in the login dialog:
 * mobile → password (known number) or register-OTP (new number),
 * then SMS login, forgot-password, the register form, or a new password.
 */
export function AuthFlow({ onSignedIn, embedded = false }: { onSignedIn?: () => void; embedded?: boolean }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const next = searchParams.get("next") ?? "/";
  const setSession = useSiteAuthStore((s) => s.setSession);

  const [step, setStep] = React.useState<Step>("mobile");
  const [mobile, setMobile] = React.useState("");
  const [otp, setOtp] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [otpResetSignal, setOtpResetSignal] = React.useState(0);
  const inFlight = React.useRef(false);
  const pushed = React.useRef<Step | null>(null);
  const skipPush = React.useRef(false);

  const finish = React.useCallback(
    (result: { token: string; user: Parameters<typeof setSession>[1] }) => {
      setSession(result.token, result.user);
      if (onSignedIn) onSignedIn();
      else router.push(next);
    },
    [next, onSignedIn, router, setSession],
  );

  const run = React.useCallback(async (task: () => Promise<void>, onError?: () => void) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setError(null);
    setIsLoading(true);
    try {
      await task();
    } catch (err) {
      setError(errorText(err, "خطایی رخ داد. دوباره تلاش کنید."));
      onError?.();
    } finally {
      inFlight.current = false;
      setIsLoading(false);
    }
  }, []);

  const clearOtp = React.useCallback(() => {
    setOtp("");
    setOtpResetSignal((value) => value + 1);
  }, []);

  React.useEffect(() => {
    if (embedded || step === "mobile") {
      pushed.current = null;
      return;
    }
    if (skipPush.current) {
      skipPush.current = false;
      pushed.current = step;
      return;
    }
    if (pushed.current === step) return;
    window.history.pushState({ ...(window.history.state as object | null), [HISTORY_KEY]: step }, "");
    pushed.current = step;
  }, [embedded, step]);

  React.useEffect(() => {
    if (embedded) return;
    const onPop = () => {
      const current = pushed.current;
      if (!current || current === "mobile") return;
      skipPush.current = true;
      setError(null);
      setStep(previousStep(current));
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [embedded]);

  const back = () => {
    setError(null);
    if (!embedded && pushed.current) {
      window.history.back();
      return;
    }
    setStep(previousStep(step));
  };

  return (
    <div className="mx-auto w-full max-w-sm">
      {step === "mobile" && (
        <AuthMobileForm
          initialMobile={mobile}
          isLoading={isLoading}
          error={error ?? undefined}
          onSubmit={(value) => {
            setMobile(value);
            void run(async () => {
              const result = await browserCoreApi.auth.check(value);
              setStep(result.isDone ? "password" : "otp-register");
            });
          }}
        />
      )}
      {step === "password" && (
        <AuthPasswordForm
          mobile={mobile}
          isLoading={isLoading}
          error={error ?? undefined}
          onBack={back}
          onSubmit={(password) => {
            void run(async () => {
              finish(await browserCoreApi.auth.login(mobile, password, SESSION));
            });
          }}
          onGetCodeBySms={() => {
            void run(async () => {
              await browserCoreApi.auth.loginOtp(mobile);
              setStep("otp-login");
            });
          }}
          onForgotPassword={() => {
            void run(async () => {
              await browserCoreApi.auth.forgetOtp(mobile);
              setStep("otp-forget");
            });
          }}
        />
      )}
      {step === "otp-login" && (
        <AuthOtpView
          mobile={mobile}
          resetSignal={otpResetSignal}
          isLoading={isLoading}
          error={error ?? undefined}
          onBack={back}
          onLoginWithPassword={() => setStep("password")}
          onForgotPassword={() => setStep("password")}
          onResend={() => {
            void run(() => browserCoreApi.auth.loginOtp(mobile).then(() => undefined));
          }}
          onSubmit={(code) => {
            void run(async () => {
              finish(await browserCoreApi.auth.verifyOtp(mobile, code, SESSION));
            }, clearOtp);
          }}
        />
      )}
      {step === "otp-register" && (
        <AuthOtpView
          mobile={mobile}
          resetSignal={otpResetSignal}
          isLoading={isLoading}
          error={error ?? undefined}
          onBack={back}
          onLoginWithPassword={() => setStep("password")}
          onForgotPassword={() => setStep("password")}
          onResend={() => {
            void run(() => browserCoreApi.auth.registerOtp(mobile).then(() => undefined));
          }}
          onSubmit={(code) => {
            void run(async () => {
              await browserCoreApi.auth.registerVerify(mobile, code);
              setOtp(code);
              setStep("register");
            }, clearOtp);
          }}
        />
      )}
      {step === "otp-forget" && (
        <AuthOtpView
          mobile={mobile}
          resetSignal={otpResetSignal}
          isLoading={isLoading}
          error={error ?? undefined}
          onBack={back}
          onLoginWithPassword={() => setStep("password")}
          onForgotPassword={() => setStep("password")}
          onResend={() => {
            void run(async () => {
              await browserCoreApi.auth.forgetOtp(mobile);
            });
          }}
          onSubmit={(code) => {
            void run(async () => {
              await browserCoreApi.auth.forgetVerify(mobile, code);
              setOtp(code);
              setStep("new-password");
            }, clearOtp);
          }}
        />
      )}
      {step === "register" && (
        <AuthRegisterForm
          isLoading={isLoading}
          error={error ?? undefined}
          onBack={back}
          onSubmit={(values: RegisterValues) => {
            void run(async () => {
              finish(
                await browserCoreApi.auth.register(
                  {
                    phone: mobile,
                    code: otp,
                    name: values.name,
                    email: values.email || undefined,
                    password: values.password,
                  },
                  SESSION,
                ),
              );
            });
          }}
        />
      )}
      {step === "new-password" && (
        <AuthNewPasswordForm
          isLoading={isLoading}
          error={error ?? undefined}
          onBack={back}
          onSubmit={(values) => {
            void run(async () => {
              await browserCoreApi.auth.resetPass(mobile, otp, values.password);
              toast.success("رمز عبور تغییر کرد. می‌توانید وارد شوید.");
              setOtp("");
              setStep("password");
            });
          }}
        />
      )}
    </div>
  );
}
